import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  WORK_AREA_TEMPLATE,
  WORK_AREA_STATUS_LABELS,
  type WorkAreaSurveyStatus,
} from "@/lib/basProcess/constants";
import { Plus, Trash2, MapPinned, ArrowRightLeft } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";
import DeferQuestionDialog from "@/components/BasProcess/DeferQuestionDialog";
import { AiPolishTextarea } from "@/components/common/AiPolishTextarea";

interface Props {
  projectId: string;
}

interface Survey {
  id: string;
  area_name: string;
  delimitation: string | null;
  adjacent_activity: string | null;
  notes: string | null;
}

interface Item {
  id: string;
  category: string;
  control_point: string;
  legal_reference: string | null;
  status: WorkAreaSurveyStatus;
  comment: string | null;
  responsible: string | null;
  transferred_risk_id: string | null;
  sort_order: number;
}

export const WorkAreaSurvey: React.FC<Props> = ({ projectId }) => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newArea, setNewArea] = useState("");
  const [newPoint, setNewPoint] = useState("");
  const [newCategory, setNewCategory] = useState("");
  // Riskkedja: arbetsberedningar som täcker en punkt i utredningen.
  const [preps, setPreps] = useState<{ id: string; name: string; source_risk_item_id: string | null }[]>([]);

  const loadPreps = React.useCallback(async () => {
    const { data } = await supabase
      .from("work_preparations")
      .select("id, name, source_risk_item_id")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false });
    setPreps(data || []);
  }, [projectId]);

  useEffect(() => {
    loadPreps();
  }, [loadPreps]);

  const linkPrep = async (itemId: string, prepId: string) => {
    const { error } = await supabase
      .from("work_preparations")
      .update({ source_risk_item_id: prepId ? itemId : null })
      .eq("id", prepId || "00000000-0000-0000-0000-000000000000");
    if (error) toast.error("Kunde inte koppla arbetsberedningen");
    else {
      toast.success("Arbetsberedningen är kopplad till kontrollpunkten");
      loadPreps();
    }
  };

  const loadSurveys = async () => {
    const { data, error } = await supabase
      .from("work_area_surveys")
      .select("id, area_name, delimitation, adjacent_activity, notes")
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    if (error) toast.error("Kunde inte läsa arbetsområden");
    const list = (data || []) as Survey[];
    setSurveys(list);
    setActiveId((prev) => prev ?? list[0]?.id ?? null);
    setLoading(false);
  };

  const loadItems = async (surveyId: string) => {
    const { data, error } = await supabase
      .from("work_area_survey_items")
      .select("id, category, control_point, legal_reference, status, comment, responsible, transferred_risk_id, sort_order")
      .eq("survey_id", surveyId)
      .order("sort_order", { ascending: true });
    if (error) toast.error("Kunde inte läsa kontrollpunkter");
    else setItems((data || []) as Item[]);
  };

  useEffect(() => {
    setLoading(true);
    loadSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (activeId) loadItems(activeId);
    else setItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const createSurvey = async () => {
    const name = newArea.trim() || "Arbetsområde";
    setBusy(true);
    const { data, error } = await supabase
      .from("work_area_surveys")
      .insert({ project_id: projectId, area_name: name })
      .select("id, area_name, delimitation, adjacent_activity, notes")
      .single();
    if (error || !data) {
      setBusy(false);
      return toast.error("Kunde inte skapa arbetsområde");
    }

    const rows = WORK_AREA_TEMPLATE.flatMap((section, si) =>
      section.items.map((it, ii) => ({
        survey_id: data.id,
        category: section.category,
        item_key: it.key,
        control_point: it.controlPoint,
        legal_reference: it.legalReference ?? null,
        explanation: it.explanation ?? null,
        sort_order: si * 100 + ii,
      }))
    );
    const { error: itemErr } = await supabase.from("work_area_survey_items").insert(rows);
    if (itemErr) toast.error("Kunde inte skapa kontrollpunkter");
    else toast.success("Arbetsområdesutredning skapad");

    setNewArea("");
    setBusy(false);
    await loadSurveys();
    setActiveId(data.id);
  };

  const updateSurvey = async (patch: Partial<Survey>) => {
    if (!activeId) return;
    setSurveys((prev) => prev.map((s) => (s.id === activeId ? { ...s, ...patch } : s)));
    const { error } = await supabase.from("work_area_surveys").update(patch).eq("id", activeId);
    if (error) toast.error("Kunde inte spara");
  };

  const deleteSurvey = async (id: string) => {
    await supabase.from("work_area_surveys").delete().eq("id", id);
    setActiveId(null);
    await loadSurveys();
  };

  const updateItem = async (id: string, patch: Partial<Item>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    const { error } = await supabase.from("work_area_survey_items").update(patch).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const removeItem = async (id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    await supabase.from("work_area_survey_items").delete().eq("id", id);
  };

  const addItem = async () => {
    if (!activeId || !newPoint.trim()) return;
    const { error } = await supabase.from("work_area_survey_items").insert({
      survey_id: activeId,
      category: newCategory.trim() || "Övrigt",
      item_key: `manual-${Date.now()}`,
      control_point: newPoint.trim(),
      sort_order: 9000 + items.length,
    });
    if (error) return toast.error("Kunde inte lägga till");
    setNewPoint("");
    setNewCategory("");
    await loadItems(activeId);
  };

  const transferToRisks = async (item: Item) => {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return toast.error("Du måste vara inloggad");

    const { data, error } = await supabase
      .from("risk_transfer_items")
      .insert({
        user_id: userId,
        project_id: projectId,
        original_risk_id: item.id,
        risk_description: item.control_point,
        assigned_to: item.responsible || "Bas-P",
        comment: item.comment || null,
      })
      .select("id")
      .single();

    if (error || !data) return toast.error("Kunde inte överföra risken");
    await updateItem(item.id, { transferred_risk_id: data.id });
    toast.success("Risken överförd till risksammanställningen");
  };

  const activeSurvey = surveys.find((s) => s.id === activeId) || null;
  const relevant = items.filter((i) => i.status !== "na");
  const assessed = relevant.filter((i) => i.status !== "not_assessed").length;
  const progress = relevant.length ? Math.round((assessed / relevant.length) * 100) : 0;
  const risks = items.filter((i) => i.status === "risk").length;

  const categories = Array.from(new Set(items.map((i) => i.category)));

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar arbetsområden...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="10"
        title="Arbetsområdesutredning"
        description="Utredning av arbetsområdets förutsättningar inför projekteringen (Bas-P)."
        icon={MapPinned}
        meta={
          activeSurvey ? (
            <div className="flex items-center gap-4">
              {risks > 0 && <Badge variant="destructive">{risks} risker</Badge>}
              <div className="w-40">
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Bedömt</span><span>{progress}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            </div>
          ) : null
        }
      />
      <Card>
        <CardContent className="space-y-4 pt-6">
          {surveys.length === 0 ? (
            <div className="text-center py-10 space-y-3">
              <MapPinned className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Ingen arbetsområdesutredning ännu.</p>
              <div className="flex justify-center gap-2">
                <Input
                  className="max-w-xs"
                  placeholder="Namn på arbetsområde"
                  value={newArea}
                  onChange={(e) => setNewArea(e.target.value)}
                />
                <Button onClick={createSurvey} disabled={busy}>
                  {busy ? "Skapar..." : "Skapa utredning"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {surveys.map((s) => (
                  <Button
                    key={s.id}
                    size="sm"
                    variant={s.id === activeId ? "default" : "outline"}
                    onClick={() => setActiveId(s.id)}
                  >
                    {s.area_name}
                  </Button>
                ))}
                <div className="flex items-center gap-2 ml-auto">
                  <Input
                    className="h-9 w-44"
                    placeholder="Nytt område"
                    value={newArea}
                    onChange={(e) => setNewArea(e.target.value)}
                  />
                  <Button size="sm" variant="outline" onClick={createSurvey} disabled={busy}>
                    <Plus className="h-4 w-4 mr-1" />Lägg till
                  </Button>
                </div>
              </div>

              {activeSurvey && (
                <div className="grid gap-2 sm:grid-cols-3">
                  <Textarea
                    rows={2}
                    placeholder="Avgränsning / gränsdragning"
                    defaultValue={activeSurvey.delimitation ?? ""}
                    key={`d-${activeSurvey.id}`}
                    onBlur={(e) => updateSurvey({ delimitation: e.target.value })}
                  />
                  <Textarea
                    rows={2}
                    placeholder="Angränsande verksamhet / tredje man"
                    defaultValue={activeSurvey.adjacent_activity ?? ""}
                    key={`a-${activeSurvey.id}`}
                    onBlur={(e) => updateSurvey({ adjacent_activity: e.target.value })}
                  />
                  <AiPolishTextarea
                    rows={2}
                    placeholder="Övriga anteckningar"
                    context="Övriga anteckningar i arbetsområdesutredning"
                    defaultValue={activeSurvey.notes ?? ""}
                    key={`n-${activeSurvey.id}`}
                    onCommit={(v) => updateSurvey({ notes: v })}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {activeSurvey &&
        categories.map((cat) => (
          <Card key={cat}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{cat}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="divide-y">
                {items
                  .filter((i) => i.category === cat)
                  .map((it) => (
                    <li key={it.id} className="py-3 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <span className="font-medium">{it.control_point}</span>
                          {it.legal_reference && (
                            <Badge variant="outline" className="ml-2 text-[10px]">{it.legal_reference}</Badge>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <DeferQuestionDialog
                            projectId={projectId}
                            sourceType="arbetsomradesutredning"
                            sourceId={activeSurvey.id}
                            questionId={it.id}
                            questionText={it.control_point}
                          />
                          <Button variant="ghost" size="icon" onClick={() => removeItem(it.id)}>
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(Object.keys(WORK_AREA_STATUS_LABELS) as WorkAreaSurveyStatus[]).map((s) => (
                          <Button
                            key={s}
                            size="sm"
                            variant={it.status === s ? (s === "risk" ? "destructive" : "default") : "outline"}
                            onClick={() => updateItem(it.id, { status: s })}
                          >
                            {WORK_AREA_STATUS_LABELS[s]}
                          </Button>
                        ))}
                        {it.status === "risk" && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={!!it.transferred_risk_id}
                            onClick={() => transferToRisks(it)}
                          >
                            <ArrowRightLeft className="h-4 w-4 mr-1" />
                            {it.transferred_risk_id ? "Överförd" : "Till risksammanställning"}
                          </Button>
                        )}
                      </div>
                      {it.status === "risk" && (
                        <div className="flex flex-wrap items-center gap-2">
                          {preps.some((p) => p.source_risk_item_id === it.id) ? (
                            <Badge className="gap-1">
                              Täckt av arbetsberedning:{" "}
                              {preps.find((p) => p.source_risk_item_id === it.id)?.name}
                            </Badge>
                          ) : (
                            <Badge variant="destructive">Ingen arbetsberedning kopplad</Badge>
                          )}
                          <select
                            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                            value={preps.find((p) => p.source_risk_item_id === it.id)?.id ?? ""}
                            onChange={(e) => linkPrep(it.id, e.target.value)}
                          >
                            <option value="">Koppla arbetsberedning…</option>
                            {preps.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="grid gap-2 sm:grid-cols-3">
                        <Input
                          placeholder="Ansvarig"
                          defaultValue={it.responsible ?? ""}
                          onBlur={(e) => updateItem(it.id, { responsible: e.target.value })}
                        />
                        <AiPolishTextarea
                          className="sm:col-span-2"
                          rows={2}
                          placeholder="Kommentar / hantering"
                          context="Kommentar om hantering av en punkt i arbetsområdesutredningen"
                          defaultValue={it.comment ?? ""}
                          onCommit={(v) => updateItem(it.id, { comment: v })}
                        />
                      </div>
                    </li>
                  ))}
              </ul>
            </CardContent>
          </Card>
        ))}

      {activeSurvey && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid gap-2 sm:grid-cols-[200px_1fr_auto]">
              <Input placeholder="Kategori" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
              <Input
                placeholder="Ny kontrollpunkt"
                value={newPoint}
                onChange={(e) => setNewPoint(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addItem()}
              />
              <Button onClick={addItem}><Plus className="h-4 w-4 mr-1" />Lägg till</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {activeSurvey && surveys.length > 1 && (
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={() => deleteSurvey(activeSurvey.id)}>
            <Trash2 className="h-4 w-4 mr-1" />Ta bort området
          </Button>
        </div>
      )}
    </div>
  );
};

export default WorkAreaSurvey;
